import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ProductCard from '../components/ProductCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Slider } from '../components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Filter, X, Search, Package } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { productAPI } from '../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ProductsPage = ({ onAddToCart }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [showFilters, setShowFilters] = useState(true);
  
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 300]);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [sortBy, setSortBy] = useState('featured');
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    fetchProducts();
  }, [selectedCategories, selectedBrands, priceRange, inStockOnly, sortBy, searchQuery, searchParams]);

  useEffect(() => {
    fetchCategoriesAndBrands();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = {
        search: searchParams.get('search') || searchQuery || undefined,
        category: searchParams.get('category') || (selectedCategories.length > 0 ? selectedCategories[0] : undefined),
        brand: searchParams.get('brand') || (selectedBrands.length > 0 ? selectedBrands[0] : undefined),
        min_price: priceRange[0],
        max_price: priceRange[1],
        in_stock: inStockOnly || undefined,
        sort_by: sortBy,
        limit: 20,
        // Fitment params from URL if present
        vehicle_region: searchParams.get('vehicle_region') || undefined,
        vehicle_make: searchParams.get('vehicle_make') || undefined,
        vehicle_model: searchParams.get('vehicle_model') || undefined,
        vehicle_year: searchParams.get('vehicle_year') || undefined,
        vehicle_trim: searchParams.get('vehicle_trim') || undefined,
        vehicle_engine: searchParams.get('vehicle_engine') || undefined,
      };

      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

      const response = await productAPI.getProducts(params);
      
      const transformedProducts = response.data.products.map(product => ({
        ...product,
        inStock: product.in_stock !== undefined ? product.in_stock : true,
        stockQuantity: product.stock_quantity || 0,
        partNumber: product.part_number || '',
        originalPrice: product.original_price || null,
        weightKg: product.weight_kg || 0
      }));
      
      setProducts(transformedProducts);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast({
        title: t('common.error'),
        description: t('products.loading'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCategoriesAndBrands = async () => {
    try {
      const categoriesResponse = await fetch(`${BACKEND_URL}/api/categories`);
      if (categoriesResponse.ok) {
        const categoriesData = await categoriesResponse.json();
        setCategories(categoriesData || []);
      }

      const brandsResponse = await fetch(`${BACKEND_URL}/api/brands`);
      if (brandsResponse.ok) {
        const brandsData = await brandsResponse.json();
        setBrands(brandsData || []);
      }
    } catch (error) {
      console.error('Error fetching categories and brands:', error);
      setCategories([]);
      setBrands([]);
    }
  };

  const clearFilters = () => {
    setSelectedCategories([]);
    setSelectedBrands([]);
    setPriceRange([0, 300]);
    setInStockOnly(false);
    setSearchQuery('');
    navigate('/products');
  };

  const handleAddToCart = (product) => {
    onAddToCart(product);
    toast({
      title: t('cart.add'),
      description: `${product.name} ${t('products.add_to_cart')}`,
    });
  };

  const toggleCategory = (category) => {
    setSelectedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [category]
    );
  };

  const toggleBrand = (brand) => {
    setSelectedBrands(prev =>
      prev.includes(brand)
        ? prev.filter(b => b !== brand)
        : [brand]
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">{t('products.all_products')}</h1>
          <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
            <p className="text-gray-600">
              {loading ? t('common.loading') : t('products.showing_x_of_y', { shown: products.length, total })}
            </p>
            
            <div className="flex gap-3 w-full md:w-auto">
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-full md:w-48">
                  <SelectValue placeholder={t('products.sort_by')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="featured">{t('products.sort_options.featured')}</SelectItem>
                  <SelectItem value="price_low">{t('products.sort_options.price_low')}</SelectItem>
                  <SelectItem value="price_high">{t('products.sort_options.price_high')}</SelectItem>
                  <SelectItem value="rating">{t('products.sort_options.rating')}</SelectItem>
                  <SelectItem value="popular">{t('products.sort_options.popular')}</SelectItem>
                </SelectContent>
              </Select>
              
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className="md:hidden"
              >
                <Filter className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        <div className="flex gap-8">
          {/* Filters Sidebar */}
          <div className={`${showFilters ? 'block' : 'hidden'} md:block w-full md:w-64 flex-shrink-0`}>
            <div className="bg-white p-6 rounded-lg shadow-sm border sticky top-24">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-bold text-gray-900">{t('products.filters')}</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="text-blue-600 hover:text-blue-700"
                >
                  {t('common.clear_all')}
                </Button>
              </div>

              {/* Search */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('common.search')}
                </label>
                <div className="relative">
                  <Input
                    type="text"
                    placeholder={t('products.search')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pr-10"
                  />
                  <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                </div>
              </div>

              {/* Categories */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  {t('products.categories')}
                </label>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {categories.map((category) => (
                    <div key={category.id} className="flex items-center">
                      <Checkbox
                        id={`category-${category.id}`}
                        checked={selectedCategories.includes(category.name)}
                        onCheckedChange={() => toggleCategory(category.name)}
                      />
                      <label
                        htmlFor={`category-${category.id}`}
                        className="ml-2 text-sm text-gray-700 cursor-pointer flex-1"
                      >
                        {category.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Brands */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  {t('products.brands')}
                </label>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {brands.slice(0, 10).map((brand) => (
                    <div key={brand.id} className="flex items-center">
                      <Checkbox
                        id={`brand-${brand.id}`}
                        checked={selectedBrands.includes(brand.name)}
                        onCheckedChange={() => toggleBrand(brand.name)}
                      />
                      <label
                        htmlFor={`brand-${brand.id}`}
                        className="ml-2 text-sm text-gray-700 cursor-pointer"
                      >
                        {brand.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Price Range */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  {t('products.price_range')}
                </label>
                <Slider
                  value={priceRange}
                  onValueChange={setPriceRange}
                  max={300}
                  step={10}
                  className="mb-2"
                />
                <div className="flex justify-between text-sm text-gray-600">
                  <span>OMR {priceRange[0]}</span>
                  <span>OMR {priceRange[1]}</span>
                </div>
              </div>

              {/* In Stock */}
              <div className="flex items-center">
                <Checkbox
                  id="in-stock"
                  checked={inStockOnly}
                  onCheckedChange={setInStockOnly}
                />
                <label
                  htmlFor="in-stock"
                  className="ml-2 text-sm text-gray-700 cursor-pointer"
                >
                  {t('products.in_stock_only')}
                </label>
              </div>
            </div>
          </div>

          {/* Products Grid */}
          <div className="flex-1">
            {loading ? (
              <div className="text-center py-12">
                <p className="text-gray-600">{t('products.loading')}</p>
              </div>
            ) : products.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {products.map((product) => (
                  <div key={product.id}>
                    {product.fitment_match && (
                      <div className="mb-2">
                        <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          {t('products.guaranteed_fit')}
                        </span>
                      </div>
                    )}

                    <ProductCard
                      product={product}
                      onAddToCart={handleAddToCart}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
                <div className="text-gray-400 mb-4">
                  <Package className="h-16 w-16 mx-auto" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {t('products.no_products')}
                </h3>
                <p className="text-gray-600 mb-6">
                  {t('products.try_adjusting')}
                </p>
                <Button onClick={clearFilters}>{t('common.clear')}</Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductsPage;
